import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { formatFullName } from '@/lib/format';

/** Admin-only: full contact values (not masked) for blocklist moderation. */
export interface DeviceIdentityName {
  firstName: string | null;
  lastName: string | null;
}

export interface DeviceSubmissionSummary {
  id: string;
  createdAt: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  submitterIp: string | null;
  isAnonymous: boolean;
  status: string;
  isShadowBanned: boolean;
}

export interface DeviceIdentities {
  deviceId: string;
  uniqueNames: DeviceIdentityName[];
  uniqueEmails: string[];
  uniquePhones: string[];
  uniqueIps: string[];
  submissionCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  submissions: DeviceSubmissionSummary[];
}

export interface DeviceDetailsModalProps {
  deviceId: string | null;
  onClose: () => void;
}

export function DeviceDetailsModal({ deviceId, onClose }: DeviceDetailsModalProps) {
  const [data, setData] = useState<DeviceIdentities | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadIdentities = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    setData(null);
    const res = await api.get<DeviceIdentities>(
      `/admin/devices/${encodeURIComponent(id)}/identities`
    );
    if (res.error) {
      setError(res.error);
    } else if (res.data) {
      setData(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (deviceId) {
      loadIdentities(deviceId);
    } else {
      setData(null);
      setError(null);
    }
  }, [deviceId, loadIdentities]);

  const isOpen = deviceId !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Device contact details"
      size="full"
      panelClassName="max-w-3xl"
    >
      {loading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {!loading && !error && data && (
        <div className="space-y-6">
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Device ID
            </p>
            <p className="mt-1 font-mono text-sm break-all text-slate-900 dark:text-white">
              {data.deviceId}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {data.submissionCount} submission{data.submissionCount === 1 ? '' : 's'}
              {data.firstSeen && data.lastSeen && (
                <>
                  {' '}
                  · First seen {new Date(data.firstSeen).toLocaleString()} · Last seen{' '}
                  {new Date(data.lastSeen).toLocaleString()}
                </>
              )}
            </p>
          </div>

          {data.submissionCount === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No prayer submissions are linked to this device ID. Submissions sent only via SMS
              may not include a device ID, or this ID may be from view tracking only.
            </p>
          ) : (
            <>
              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Names
                </h3>
                {data.uniqueNames.length === 0 ? (
                  <p className="text-sm text-slate-500">No names on file.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-1">
                    {data.uniqueNames.map((n, i) => (
                      <li key={`${n.firstName}-${n.lastName}-${i}`}>
                        {formatFullName(n.firstName, n.lastName) || '—'}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Email addresses
                </h3>
                {data.uniqueEmails.length === 0 ? (
                  <p className="text-sm text-slate-500">No emails on file.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-1">
                    {data.uniqueEmails.map((email) => (
                      <li key={email}>
                        <a
                          href={`mailto:${email}`}
                          className="text-primary-600 hover:underline dark:text-primary-400"
                        >
                          {email}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Phone numbers
                </h3>
                {data.uniquePhones.length === 0 ? (
                  <p className="text-sm text-slate-500">No phone numbers on file.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-1">
                    {data.uniquePhones.map((phone) => (
                      <li key={phone}>
                        <a
                          href={`tel:${phone.replace(/\D/g, '')}`}
                          className="text-primary-600 hover:underline dark:text-primary-400"
                        >
                          {phone}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  IP addresses
                </h3>
                {data.uniqueIps.length === 0 ? (
                  <p className="text-sm text-slate-500">No IP addresses on file.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-1 font-mono">
                    {data.uniqueIps.map((ip) => (
                      <li key={ip}>{ip}</li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Submission history
                </h3>
                <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Name</th>
                        <th className="px-3 py-2 text-left font-medium">Email</th>
                        <th className="px-3 py-2 text-left font-medium">Phone</th>
                        <th className="px-3 py-2 text-left font-medium">IP</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {data.submissions.map((s) => (
                        <tr key={s.id}>
                          <td className="px-3 py-2 align-middle text-slate-500 whitespace-nowrap">
                            {new Date(s.createdAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            {s.isAnonymous ? (
                              <span className="text-slate-500 italic">Anonymous</span>
                            ) : (
                              formatFullName(s.firstName, s.lastName) || '—'
                            )}
                          </td>
                          <td className="px-3 py-2 align-middle text-xs">{s.email ?? '—'}</td>
                          <td className="px-3 py-2 align-middle text-xs">{s.phone ?? '—'}</td>
                          <td className="px-3 py-2 align-middle text-xs font-mono">
                            {s.submitterIp ?? '—'}
                          </td>
                          <td className="px-3 py-2 align-middle text-xs">
                            {s.status}
                            {s.isShadowBanned && (
                              <span className="ml-1 text-amber-600 dark:text-amber-400">
                                (shadow)
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      )}
      <div className="mt-6 flex justify-end">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
